import videojs from 'video.js';

import AudioDescriptionsPlayer from './audio-descriptions/audio-descriptions-player.js';

const _audioPlayer = new WeakMap();
let localize;

/**
 * The AudioDescriptions module.
 * @member {AudioDescriptionsPlugin} audioDescriptions
 * @memberof MediasitePlayer#
 */

/**
 * Configures an audio descriptions track to play along with the main source video. Playback and audio volume are kept in sync with the main source video automatically.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class AudioDescriptionsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        player.ready(() => {
            player.collapsedButtons?.push({
                pluginName: this.name,
                label: localize('Audio Descriptions'),
                buttonName: 'AudioTrackButton',
                class: 'vjs-audio-button'
            });
        });
    }

    /**
     * The source URL of an audio descriptions file to play along with the main media. If <code>null</code>, the existing audio descriptions track will be removed (if there is one).
     * @type {string}
     * @example
     * player.audioDescriptions.src = '/path/to/audio/file.mp3';
     */
    set src(src) {
        const player = this.player;
        localize = player.localize?.bind(player);
        let audioPlayer = _audioPlayer.get(this);

        const audioTrackList = player.audioTracks();
        const existingTrack = getAudioDescriptionsTrack(player);
        const mainTrack = getMainTrack(player);
        mainTrack.label = localize('Main');

        if (src) {
            // Make sure there's an audio player component for the audio descriptions
            if (!audioPlayer) {
                audioPlayer = new AudioDescriptionsPlayer(player);
                audioPlayer.enabled = false;

                audioTrackList.addEventListener('change', () => {
                    for (let i = 0; i < audioTrackList.length; i++) {
                        if (audioTrackList[i].kind === 'main-desc' && audioTrackList[i].enabled) {
                            audioPlayer.enabled = true;
                        }
                        else {
                            audioPlayer.enabled = false;
                        }
                    }
                });

                _audioPlayer.set(this, audioPlayer);
                player.addChild(audioPlayer);
            }

            // Add the track to the player, if it does not already exist
            if (!existingTrack) {
                audioTrackList.addTrack(new videojs.AudioTrack({
                    kind: 'main-desc',
                    label: localize('Audio Descriptions'),
                    language: 'en'
                }));
            }

            audioPlayer.src = src;
        }
        else {
            removeAudioComponent(player);
            removeAudioDescriptionsTrack(player);
        }
    }

    get src() {
        return _audioPlayer.get(this)?.src;
    }

    dispose() {
        removeAudioComponent(this.player);
        removeAudioDescriptionsTrack(this.player);

        super.dispose();
    }
}

/**
 * @private
 * @param {videojs.Player} player
 */
function getMainTrack(player) {
    return Array.from(player.audioTracks())
        .find(track => track.kind === 'main');
}

/**
 * @private
 * @param {videojs.Player} player
 */
function getAudioDescriptionsTrack(player) {
    return Array.from(player?.audioTracks() ?? [])
        .find(track => track.kind === 'main-desc');
}

/**
 * @private
 * @param {videojs.Player} player
 */
function removeAudioDescriptionsTrack(player) {
    const audioTrackList = player?.audioTracks();
    const existingTrack = getAudioDescriptionsTrack(player);

    if (existingTrack) {
        audioTrackList.removeTrack(existingTrack);

        // Enable the main track so Video.js UI in the ControlBar properly resets
        Array.from(audioTrackList).find(track => track.kind === 'main').enabled = true;
    }
}

/**
 * @private
 * @param {videojs.Player} player
 */
function removeAudioComponent(player) {
    const audioPlayer = _audioPlayer.get(this);
    if (audioPlayer) {
        audioPlayer.src = null;
        player.removeChild(audioPlayer);
        _audioPlayer.delete(this);
    }
}

export default AudioDescriptionsPlugin;
